import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { ComparePackagesTable } from "app/components/React/AfterTrialHook/ComparePackages/ComparePackagesTable";
import {
    availableCurrencies,
    BulletsLeft,
    CURRENCY,
    RightBulletsKeys,
} from "app/components/React/AfterTrialHook/ComparePackages/tableData";
import {
    BillingMount,
    BillingMountText,
    BillingTitle,
    Bullet,
    BulletContainer,
    BulletsRightContainer,
    BulletsRightText,
    BulletsRightTitle,
    BulletsTitle,
    BulletText,
    LeftSide,
    Price,
    RightSide,
    RightSideSubTitle,
    RightSideTitle,
    RightSideTitleContainer,
    SubTitle,
    TableButtonText,
    Title,
} from "app/components/React/AfterTrialHook/StyledComponents";
import { FlexRow } from "app/components/React/Table/cells/DomainListWithTooltip/styled";
import { translate } from "app/filters/i18nFilter";
import type { ICurrency, IPackagesPrice } from "app/pages/digital-suite/home-page/components/AfterTrialPopup/types";
import {
    getAvailableCurrencies,
    getCurrency,
    getDefaultCurrency,
    getPriceByCurrency,
} from "app/pages/digital-suite/home-page/components/AfterTrialPopup/utils";
import { AssetsService } from "app/services/AssetsService";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import { isAfterTrialCC } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { trackEvents } from "./utils";
interface IAfterTrialHookPopup {
    packages: IPackagesPrice;
}

export const AfterTrialHookPopup: FC<IAfterTrialHookPopup> = ({ packages }) => {
    const [isComparePackagesOpen, setIsComparePackagesOpen] = useState<boolean>(false);

    const isCCUser = isAfterTrialCC(swSettings);

    const currencyCodes = getAvailableCurrencies(packages, availableCurrencies);

    const defaultCurrency = getDefaultCurrency(currencyCodes, CURRENCY);

    const currentCurrency = useMemo<ICurrency>(() => {
        return getCurrency(defaultCurrency);
    }, [defaultCurrency]);

    const packagePrice = getPriceByCurrency(packages?.CompetitiveIntelligenceAnnually, defaultCurrency).Price / 12;

    const onUpgradeClick = () => {
        trackEvents("click", "package", isCCUser);
        goToPackagesPage({ currency: CURRENCY });
    };
    useEffect(() => {
        trackEvents("open", "hook", isCCUser);
    }, []);

    const onClickOpenComparePackages = () => {
        trackEvents("click", "compare packages", isCCUser);
        setIsComparePackagesOpen(true);
    };

    const onClickBack = () => {
        trackEvents("click", "back from compare packages", isCCUser);
        setIsComparePackagesOpen(false);
    };
    return (
        <FlexRow>
            {!isComparePackagesOpen ? (
                <LeftSide>
                    <Title>
                        {translate(
                            isCCUser
                                ? "pro.after.trial.hook.popup.left.title.cc"
                                : "pro.after.trial.hook.popup.left.title",
                        )}
                    </Title>
                    <SubTitle>
                        {translate(
                            isCCUser
                                ? "pro.after.trial.hook.popup.left.subtitle.cc"
                                : "pro.after.trial.hook.popup.left.subtitle",
                        )}
                    </SubTitle>
                    <BulletsTitle>{translate("pro.after.trial.hook.popup.left.bullets.title")}</BulletsTitle>
                    {BulletsLeft.map((item) => {
                        return (
                            <BulletContainer>
                                <Bullet />
                                <BulletText>{translate(item)}</BulletText>
                            </BulletContainer>
                        );
                    })}
                    <a onClick={onClickOpenComparePackages}>
                        <TableButtonText>
                            {translate("pro.after.trial.hook.popup.left.table.button.text")}
                        </TableButtonText>
                    </a>
                    <img
                        src={AssetsService.assetUrl(`/images/trial-modal/after_trial_popup.png`)}
                        style={{
                            position: "absolute",
                            right: "20px",
                            bottom: "8px",
                            width: "226px",
                        }}
                        alt={""}
                    />
                </LeftSide>
            ) : (
                <ComparePackagesTable
                    onClickBack={onClickBack}
                    isCCUser={isCCUser}
                />
            )}
            <RightSide>
                <RightSideTitleContainer>
                    <RightSideTitle>
                        {translate(
                            isCCUser
                                ? "pro.after.trial.hook.popup.right.title.cc"
                                : "pro.after.trial.hook.popup.right.title",
                        )}
                    </RightSideTitle>
                    <RightSideSubTitle>{translate("pro.after.trial.hook.popup.right.subtitle")}</RightSideSubTitle>
                </RightSideTitleContainer>
                <BillingTitle>{translate("pro.after.trial.hook.popup.right.billing.title")}</BillingTitle>
                <BillingMount>
                    <Price>
                        {currentCurrency.sign}
                        {packagePrice}
                    </Price>
                    <BillingMountText>/{translate("pro.after.trial.hook.popup.right.billing.month")}</BillingMountText>
                </BillingMount>
                <Button
                    onClick={onUpgradeClick}
                    label={translate("pro.after.trial.hook.popup.right.button.upgrade.text")}
                    width="280px"
                />
                <BulletsRightTitle>{translate("pro.after.trial.hook.popup.right.bullets.title")}</BulletsRightTitle>
                {RightBulletsKeys.map((item) => {
                    return (
                        <BulletsRightContainer>
                            <img
                                src={AssetsService.assetUrl(`/images/trial-modal/open.svg`)}
                                style={{
                                    width: 16,
                                    height: 16,
                                }}
                                alt={""}
                            />
                            <BulletsRightText>{translate(item)}</BulletsRightText>
                        </BulletsRightContainer>
                    );
                })}
            </RightSide>
        </FlexRow>
    );
};
